<template lang="pug">
.mapped-fields
    .empty(v-if="!question") {{ $t(`${baseTranslationPath}.select_question_hint`) }}
    template(v-else)
        .mf-header
            .mf-title {{ question.prompt || question.question_key }}
            .mf-sub {{ $t(`${baseTranslationPath}.mapped_fields_count`, { count: mappedFields.length }) }}
        .mf-empty(v-if="!mappedFields.length") {{ $t(`${baseTranslationPath}.no_mapped_fields`) }}
        .mf-list
            .mf-item(
                v-for="(field, index) in mappedFields"
                :key="field.id"
                :class="{ active: field.id === selectedFieldId }"
                @click="$emit('select-field', field)"
            )
                .mf-index {{ index + 1 }}
                font-awesome-icon.mf-icon(:icon="getFieldIconType(field).icon")
                .mf-body
                    .mf-label {{ field.field_name || field.label || field.field_key }}
                    .mf-meta
                        span.tag {{ field.field_type }}
                        span.tag(v-if="field.interaction_class") {{ field.interaction_class }}
                        span.tag(v-if="field.required") {{ $t(`${baseTranslationPath}.required`) }}
                font-awesome-icon.mf-settings(
                    :icon="['fas', 'ellipsis-vertical']"
                    :title="$t(`${baseTranslationPath}.field_settings_title`)"
                    @click.stop="$emit('open-field-settings', field)"
                )
</template>
<script>
import get from 'lodash/get';
import { fieldIconTypes } from './fieldUtil';

export default {
  name: 'MappedFields',
  props: {
    // selected question row (form_questions), or null.
    question: {
      type: Object,
      default: null,
    },
    // all form_fields rows for the form.
    fields: {
      type: Array,
      default: () => [],
    },
    selectedFieldId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['select-field', 'open-field-settings'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
    };
  },
  computed: {
    // fields whose question_ref points at this question, in question_position order.
    mappedFields() {
      if (!this.question) {
        return [];
      }
      return this.fields
        .filter(f => get(f, 'question_ref') === this.question.id)
        .sort((a, b) => get(a, 'question_position', 0) - get(b, 'question_position', 0));
    },
  },
  methods: {
    getFieldIconType(field) {
      return fieldIconTypes(get(field, 'field_type'));
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.mapped-fields {
  height: 100%;
  overflow-y: auto;
  padding: 14px;
}

.empty,
.mf-empty {
  color: $aiq-color-text-secondary;
  font-size: 13px;
  padding: 16px;
  text-align: center;
}

.mf-header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid $aiq-tab-content-border;
}

.mf-title {
  font-size: 16px;
  font-weight: 700;
}

.mf-sub {
  font-size: 12px;
  color: $aiq-color-text-secondary;
  margin-top: 2px;
}

.mf-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 1px solid $aiq-icon-primary;
  border-radius: 5px;
  margin-bottom: 8px;
  cursor: pointer;
  background-color: $aiq-bgc-list-item;

  &:hover {
    background-color: $aiq-tab-content-background;
    border-color: $aiq-tab-content-background;
  }

  &.active {
    background-color: $aiq-tab-content-background;
    border-color: $aiq-bgc-button-primary;
  }
}

.mf-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.mf-icon {
  color: $aiq-input-border-hover;
  font-size: 16px;
}

.mf-label {
  font-size: 14px;
  font-weight: 600;
}

.mf-body {
  min-width: 0;
  flex: 1;
}

.mf-settings {
  color: $aiq-input-border-hover;
  font-size: 15px;
  padding: 4px 6px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: $aiq-bgc-button-primary;
  }
}

.mf-meta {
  display: flex;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.tag {
  font-size: 11px;
  color: $aiq-input-border-hover;
  border: 1px solid $aiq-icon-primary;
  border-radius: 3px;
  padding: 0 5px;
}
</style>
